"use strict";

import { Grid, GridFromArray, BoundedGrid } from './Grid';
import { colorSchemes, colorSchemeByMod, ColorScheme } from './ColorScheme.ts';
import { tileShapes } from './tileShapes.ts';
import { compute_operations_for_level, compute_operations, vector_multiply_matrix, vector_simplify_arithmetic, level_get_arithmetic, level_check_solution, vector_sum, get_gaussian_solution_for_level, get_level_full_identifier } from './algo';
import { generate_id } from '../utils/helpers.ts';
import { storage } from './storage.ts';
import { Book } from './Book';
import { Geometry } from './algo';
import { SquareTileShape } from './SquareTileShape';
import { assert } from '../utils/helpers.ts';



export class Level {
  colorScheme: ColorScheme;
  tileShape: SquareTileShape;
  tiles: BoundedGrid<number>;
  par: number | null;
  text: string;
  shortName: string | null;
  longName: string | null;
  index: number;
  isIcon: boolean;
  isCustom: boolean;
  hidden: boolean;
  mode: "normal"|"challenge";
  id: string;
  solutions: number[][];
  // TODO: split this into multiple fields, like solutionMethod, isOptimal, etc.
  solutionType: "running"|"submitted"|"gaussian"|"impossible"|"optimal"|"exhaustive"|"manual"|"custom";
  book: Book | null;
  geometry: Geometry | null;

  constructor(size: number) {
    const grid = Grid.fill(size, size, 1);
    this.colorScheme = colorSchemes.BW;
    this.tileShape = tileShapes.square;
    this.tiles = grid;
    this.par = 0;
    this.text = "";
    this.shortName = null;
    this.longName = null;
    this.index = -1;
    this.isIcon = false;
    this.isCustom = false;
    this.hidden = false;
    this.mode = "normal";
    this.id = generate_id("level");
    this.book = null;
    this.geometry = null;

    let operations = compute_operations_for_level(this);
    let m = operations.length;

    this.solutions = [new Array(m).fill(0)];
    this.solutionType = "running";
    
  }

  static empty(size: number): Level {
    return new Level(size);
  }

  static fromJsonObject(json: any): Level {
    const level = new Level(1);
    level.colorScheme = colorSchemes.BW; //colorSchemes[json.colorScheme];
    level.tileShape = tileShapes.square; // tileShapes[json.tileShape];
    level.tiles = level.tileShape.gridFromJsonObject(json.tiles);
    level.par = json.par;
    level.text = json.text || "";
    // Backward compatibility: if title exists but shortName doesn't, use title as shortName
    level.shortName = json.shortName ?? json.title ?? null;
    level.longName = json.longName ?? null;
    level.index = json.index;
    level.isIcon = !!json.isIcon;
    level.hidden = !!json.hidden;
    level.mode = json.mode || "normal";
    
    // Auto-generate longName for weekly challenge levels if not set
    if (!level.longName && level.shortName && level.mode === "challenge" && level.shortName.startsWith("Weekly #")) {
      level.longName = level.shortName.replace("Weekly", "Weekly Challenge");
    }
    
    // Auto-generate longName for daily levels if not set
    if (!level.longName && level.shortName && level.shortName.startsWith("Daily #")) {
      level.longName = level.shortName.replace("Daily", "Daily Level");
    }
    
    if (json.id) {
      level.id = json.id;
    } else {
      level.id = generate_id("level");
    }
    let hasValidSolution = false;
    if (json.solutions) {
      // Only include valid solutions from json.solutions
      level.solutions = json.solutions
        .map((sol: number[]) => sol.slice())
        .filter((sol: number[]) => level_check_solution(level, sol));
      level.solutionType = json.solutionType;
      hasValidSolution = level.solutions.length > 0;
    } else if (json.solutionVector) {
      // Backward compatibility: convert old solutionVector to solutions array
      level.solutions = [json.solutionVector.slice()];
      level.solutionType = json.solutionType;
      hasValidSolution = level_check_solution(level, level.solutions[0]);
    }
    if (!hasValidSolution) {
      // A lot of code relies on the solution vector being set,
      // so I compute it here, even if it is suboptimal.
      // However if it's not optimal.
      compute_gaussian_solution(level);

      // Sanity check, that the computed solution makes sense
      assert(check_all_solutions(level));

      level.par = null;
    } else {
      if (level.mode == "challenge") {
        level.par = null;
      } else {
        level.par = vector_sum(level.solutions[0]);
      }
    }

    return level;
  }

  static fromCompact(s: string): Level | null {
    let [geo, arith, tOrV, dataStr] = s.split("$");
    if (!geo.startsWith("s_")) {
      return null;
    }
    let [_1, wStr, hStr] = geo.split("_");
    let w = Number(wStr);
    let h = Number(hStr);

    if (!arith.startsWith("m_")) {
      return null;
    }
    let [_2, modStr] = arith.split("_");
    let mod = Number(modStr);

    let data: number[];
    if (dataStr.startsWith("_")) {
      data = dataStr.slice(1).split("_").map(Number);
    } else {
      data = Array.from(dataStr).map(Number);
    }

    const level = new Level(1);
    level.colorScheme = colorSchemeByMod[mod];
    level.tileShape = tileShapes.square;
    level.text = "";
    level.shortName = null;
    level.longName = null;
    level.index = -1;
    level.isIcon = false;
    level.mode = "normal";
    // TODO: does it make sense to not add any numbers or anything?
    level.id = "custom";

    if (tOrV == "v") {
      let operations = compute_operations({
        type: "square",
        width: w,
        height: h,
      });

      let solution = data;

      let reach = vector_multiply_matrix(solution, operations, level_get_arithmetic(level));

      reach = reach.map(a => a + 1);
      level.tiles = Grid.usingFlatArray(reach, w, h);

      level.solutions = [solution];
      level.solutionType = "submitted";
    } else {
      return null;
    }

    // Sanity check: verify all solutions are valid
    assert(check_all_solutions(level));
    level.par = vector_sum(level.solutions[0]);

    return level;
  }
  toJsonObject(): Record<string, any> {
    const json: any = {};
    json.colorScheme = this.colorScheme.name;
    json.tileShape = this.tileShape.name;
    json.tiles = this.tiles.to2dArray(); //TODO: should be tileShape.gridToJson...
    json.par = this.par;
    if (this.text) {
      json.text = this.text;
    }
    if (this.shortName) {
      json.shortName = this.shortName;
    }
    if (this.longName) {
      json.longName = this.longName;
    }
    if (this.isIcon) {
      json.isIcon = this.isIcon;
    }
    if (this.hidden) {
      json.hidden = this.hidden;
    }
    if (this.mode && this.mode !== "normal") {
      json.mode = this.mode;
    }
    json.index = this.index;
    json.id = this.id;

    json.solutions = this.solutions.map(sol => sol.slice());
    json.solutionType = this.solutionType;

    json.__type__ = "Level";
    return json;
  }

  // Clones level, but keeps the same id.
  clone(): Level {
    const level = new Level(1);
    level.copyFrom(this);
    return level;
  }

  copyFrom(otherLevel: this): void {
    this.colorScheme = otherLevel.colorScheme;
    this.tileShape = otherLevel.tileShape;
    this.tiles = otherLevel.tiles.clone();
    this.par = otherLevel.par;
    this.text = otherLevel.text;
    this.shortName = otherLevel.shortName;
    this.longName = otherLevel.longName;
    // TODO: does it make sense to copy the index?
    this.index = otherLevel.index;
    this.id = otherLevel.id;
    this.isIcon = otherLevel.isIcon;
    this.isCustom = otherLevel.isCustom || false;
    this.hidden = otherLevel.hidden || false;
    this.mode = otherLevel.mode || "normal";
    if (otherLevel.solutions) {
      this.solutions = otherLevel.solutions.map(sol => sol.slice());
    } else {
      this.solutions = [];
    }
    this.solutionType = otherLevel.solutionType;
    if (otherLevel.book !== undefined) {
      this.book = otherLevel.book;
    }
  }

  getFullIdentifier(): string {
    // Contains the id of the level, as well as information that can be used to recreate the level.
    return get_level_full_identifier(this);
  }

  getBestNumMoves(): number | null {
    return storage.getLevelBest(this);
  }

  showsDemo(): boolean {
    return this.id == "level_1693531796434";
  }

  getPar(): number | null {
    if (!this.solutions || this.solutions.length === 0) {
      return null;
    }
    let par = vector_sum(this.solutions[0]);
    return par;
  }
}

export function check_all_solutions(level: Level): boolean {
  if (level.solutionType == "impossible") {
    return level.solutions.length === 0;
  }
  return level.solutions.length > 0 &&
      level.solutions.every(sol => level_check_solution(level, sol));
}

export function compute_gaussian_solution(level: Level): void {
  let sol = get_gaussian_solution_for_level(level);
  if (sol) {
    level.solutions = [sol];
    level.solutionType = "gaussian";
  } else {
    level.solutions = [];
    level.solutionType = "impossible";
  }
}
